import { DownOutlined, PlusOutlined } from '@ant-design/icons'
import { Avatar, Menu, Typography } from 'antd'
import { useRouter } from 'next/router'
import { useIntl } from 'react-intl'

import { WorkspacesMenuItems } from './WorkspacesMenuItems'
import { getMenuItem, MenuItem } from '@/components/ui/common/MenuItem'
import { useWorkspaceContext } from '@/context/WorkspaceContext'
import { buildI18nFunction } from '@/utils/messages'
import { isWorkspaceAdmin } from '@/utils/workspaces'

import styles from './WorkspaceMenuStyles.module.css'

type WorkspaceMenuProps = {
  collapsed: boolean
}

export function WorkspaceMenu({ collapsed }: WorkspaceMenuProps) {
  const {
    workspaces,
    setWorkspaceId,
    workspace: currentWorkspace,
    user,
  } = useWorkspaceContext()

  const hasOneWorkspace = workspaces.total_elements === 1
  const t = buildI18nFunction(useIntl().formatMessage)
  const isUserAdmin = isWorkspaceAdmin(user, currentWorkspace.id)
  const router = useRouter()

  const workspacesMenuItems = [
    !hasOneWorkspace && isUserAdmin && { type: 'divider' },
    ...(!hasOneWorkspace
      ? [
          {
            className: 'workspaces',
            children: WorkspacesMenuItems(currentWorkspace, setWorkspaceId),
            type: 'group',
          },
        ]
      : []),
  ] as MenuItem[]

  const workspaceSettingsMenuItems = [
    isUserAdmin && {
      className: 'workspaceSettings',
      ...getMenuItem(
        <Typography.Text type="secondary">
          {t('WorkspaceDropdown.group.label')}
        </Typography.Text>,
        'current-workspace',
        null,
        [
          {
            'data-cy': 'current-workspace',
            key: `general`,
            label: (
              <span key="general">{t(`SettingsWorkspace.tab.general`)}</span>
            ),
            onClick: () => router.push(`/settings/general`),
          },
          {
            'data-cy': 'current-workspace',
            key: 'connections',
            label: (
              <span key="connections">
                {t(`SettingsWorkspace.tab.connections`)}
              </span>
            ),
            onClick: () => router.push(`/settings/connections`),
          },
          {
            'data-cy': 'current-workspace',
            key: 'users',
            label: <span key="users">{t(`SettingsWorkspace.tab.users`)}</span>,
            onClick: () => router.push(`/settings/users`),
          },
        ],
        'group'
      ),
    },
  ]

  const menuItems: MenuItem[] = [
    {
      className: 'mainMenuItem',
      'data-cy': `current-workspace-dropdown`,
      ...getMenuItem(
        <span className="name" data-cy="current-workspace-name">
          {!collapsed && (
            <>
              {currentWorkspace?.name}

              <DownOutlined className={styles.currentWorkspaceArrow} />
            </>
          )}
        </span>,
        'workspaceMenu',
        <Avatar
          src={currentWorkspace?.avatar_url ?? null}
          icon={
            currentWorkspace?.avatar_url ? null : isUserAdmin ? (
              <PlusOutlined />
            ) : null
          }
          size={'large'}
          className="menu-item-title"
          style={{
            cursor: !currentWorkspace?.avatar_url && 'pointer',
            fontSize: '24px',
            top: '12px',
            left: '4px',
          }}
          onClick={
            !currentWorkspace?.avatar_url && isUserAdmin
              ? () => router.push(`/settings/general`)
              : null
          }
        />,
        [...workspaceSettingsMenuItems, ...workspacesMenuItems]
      ),
    },
  ]

  if (isUserAdmin || !hasOneWorkspace)
    return (
      <Menu
        disabledOverflow
        items={menuItems}
        mode={'horizontal'}
        multiple={false}
        rootClassName={styles.workspaceMenu}
        selectable={false}
      />
    )

  return (
    <Menu
      rootClassName={styles.workspaceMenu}
      items={[
        {
          className: 'mainMenuItem',
          'data-cy': `current-workspace-dropdown`,
          ...getMenuItem(
            <span className="name" data-cy="current-workspace-name">
              {!collapsed && <>{currentWorkspace?.name}</>}
            </span>,
            'workspaceMenu',
            <Avatar
              src={currentWorkspace?.avatar_url ?? null}
              icon={
                currentWorkspace?.avatar_url ? null : isUserAdmin ? (
                  <PlusOutlined />
                ) : null
              }
              size={'large'}
              className="menu-item-title"
              style={{
                cursor: !currentWorkspace?.avatar_url && 'pointer',
                fontSize: '24px',
                top: '12px',
                left: '4px',
              }}
              onClick={
                !currentWorkspace?.avatar_url && isUserAdmin
                  ? () => router.push(`/settings/general`)
                  : null
              }
            />,
            [...workspaceSettingsMenuItems, ...workspacesMenuItems]
          ),
        },
      ]}
      disabledOverflow
      mode={'horizontal'}
      multiple={true}
      selectable={false}
    />
  )
}
